import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import config from '../../config';
import {
  Breadcrumb,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
  Upload,
  message,
  notification,
  Checkbox,
  Avatar,
  Tooltip,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  tailFormItemLayout,
  formItemLayout,
  unexpectedError,
  handleBackendFormErrors,
  backButton,
  getBase64,
  beforeUpload,
} from '../../common_functions';
import UploadTrigger from '../../common_functions/UploadTrigger';

const Stamps = props => {
  const { section, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [stamps, setStamps] = useState({});
  const formRef = useRef(null);

  // Preview Image
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(false);
  const [previewTitle, setPreviewTitle] = useState(false);
  const [image, setImage] = useState(null);
  const [artist, setArtist] = useState([]);
  const [parentStamps, setParentStamps] = useState([]);
  const [filteredStampData, setFilteredStampData] = useState([]);
  const [isSubgroup, setIsSubgroup] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        /** Get artist name */
        const artistFullName = await props.getItems('Artist', {
          fields: ['id', 'First_Name', 'last_name'],
          sort: '-id',
          limit: -1,
        });
        setArtist(artistFullName.data);

        if (id !== 'new') {
          const { data } = await props.getItem('stamps', id, {
            fields: [
              'id,stamp_name',
              'period_end_date_year,period_start_date_year',
              'description,picture.id,picture.filename_download',
              'artist,height,width',
              'stamp_notation,city_width',
              'source_name,source_reference',
              'parent_stamp,is_subgroup',
            ],
          });
          data.period_start_date_year = data.period_start_date_year ? dayjs(data.period_start_date_year) : null;
          data.period_end_date_year = data.period_end_date_year ? dayjs(data.period_end_date_year) : null;
          if (data?.picture?.id) {
            const fileData = await props.getFile(data.picture.id);
            setImage([
              {
                uid: data.picture.id,
                name: data.picture.filename_download,
                url: fileData,
              },
            ]);
          }
          if (data.is_subgroup) {
            showParentsStamps(null, data.is_subgroup);
          }
          setStamps(data);
          formRef.current.setFieldsValue(data);
        }
      } catch (error) {
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [section, id]);

  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };
  let onFinish = async values => {
    setLoading(true);
    values.period_start_date_year = values.period_start_date_year ? moment.utc(values.period_start_date_year).local().format() : null;
    values.period_end_date_year = values.period_end_date_year ? moment.utc(values.period_end_date_year).local().format() : null;
    values.parent_stamp = values?.is_subgroup ? values.parent_stamp : null;
    if (values?.picture?.[0]?.originFileObj) {
      const { originFileObj } = values.picture[0];
      const formData = new FormData();
      formData.append('folder', process.env.stamp_folder);
      formData.append('file', originFileObj);
      let {
        data: { id: fileID },
      } = await props.saveFile(formData, stamps?.picture?.id);
      values.picture = fileID;
    }

    let newState = { ...stamps, ...values };

    try {
      const response = await props.saveItem('stamps', newState);
      if (response) {
        setLoading(false);
        notification['success']({
          message: 'Stamps saved successfully!',
          duration: 2,
        });
        backButton(props, `/settings/stamps`);
      }
    } catch (errorResponse) {
      handleBackendFormErrors(formRef, errorResponse);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => setPreviewVisible(false);
  /**Handle file change**/
  const uploadFile = async ({ file, fileList }) => {
    try {
      setLoading(true);
      if (!(file instanceof File)) return;
      const url = await getBase64(fileList[0].originFileObj);
      setImage([
        {
          uid: '-1',
          name: file.name,
          status: 'done',
          url: url,
        },
      ]);
      setLoading(false);
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    }
  };
  const handlePreview = async file => {
    setPreviewImage(file.url);
    setPreviewVisible(true);
    setPreviewTitle(file.name);
  };
  // Custom validation function for period start date year
  const validateStartDate = (_, value) => {
    if (!value) {
      return Promise.reject();
    }
    // Get the period end date year value from the form
    const endDateYear = formRef.current.getFieldValue('period_end_date_year');
    if (endDateYear && value.isAfter(endDateYear)) {
      return Promise.reject(new Error('Period start date must be before the end date!'));
    }
    return Promise.resolve();
  };
  // Custom validation function for period end date year
  const validateEndDate = (_, value) => {
    if (!value) {
      return Promise.reject();
    }
    // Get the period start date year value from the form
    const startDateYear = formRef.current.getFieldValue('period_start_date_year');
    if (startDateYear && value.isBefore(startDateYear)) {
      return Promise.reject(new Error('Period end date must be after the start date!'));
    }
    return Promise.resolve();
  };
  const normFile = e => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const handleStampSearch = inputValue => {
    const filteredData = parentStamps.filter(el => el.stamp_name.toLowerCase().includes(inputValue.toLowerCase()));
    setFilteredStampData(filteredData);
  };

  const getParentStamps = async () => {
    setLoading(true);
    const { data: sData } = await props.getItems('stamps', {
      fields: ['id', 'stamp_name', 'picture'],
      filters: { is_subgroup: { _eq: false } },
      limit: -1,
    });

    const stampsWithImages = await Promise.all(
      sData.map(async stamp => {
        if (stamp?.picture) {
          const imageUrl = await props.getFile(stamp.picture);
          return {
            ...stamp,
            picture: imageUrl,
          };
        }
        return stamp;
      })
    );
    setParentStamps(stampsWithImages);
    setFilteredStampData(stampsWithImages);
    setLoading(false);
  };
  const showParentsStamps = async (e = null, subgroup = null) => {
    let isStampSubGroup = e?.target?.checked || subgroup;
    if (isStampSubGroup) {
      await getParentStamps();
    }
    setIsSubgroup(isStampSubGroup);
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb items={[{ title: "Settings", href: "/settings/stamps" }, { title: "Stamps" }, { title: id != 'new' ? `Edit Stamps` : 'Add Stamps' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {id != 'new' ? `Edit Stamps: ${id}` : 'Add Stamps'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row gutter={24}>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Form.Item name="is_subgroup" label="" valuePropName="checked">
                    <Checkbox onChange={showParentsStamps} checked={isSubgroup}>
                      Stamp Subgroup?
                    </Checkbox>
                  </Form.Item>
                </Col>
                {isSubgroup ? (
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item
                      name="parent_stamp"
                      label="Parent Stamp"
                      rules={[
                        {
                          required: true,
                          message: 'Parent stamp is required!',
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        allowClear
                        virtual={false}
                        filterOption={false}
                        placeholder="Select stamp"
                        onSearch={handleStampSearch}
                        style={{ textTransform: 'capitalize' }}
                      >
                        {filteredStampData?.map(el => (
                          <Select.Option key={el.id} value={el.id} onMouseEnter={() => {}} onMouseLeave={() => {}}>
                            <Row justify="space-between">
                              <Col>{el.stamp_name}</Col>
                              <Col>
                                <Tooltip
                                  title={
                                    <div className="avatar-container">
                                      <img src={el.picture} alt="Avatar" className="avatar-image" />
                                    </div>
                                  }
                                  destroyTooltipOnHide={true}
                                >
                                  {el?.picture ? (
                                    <Avatar src={el.picture} shape="square" />
                                  ) : (
                                    <Avatar icon={<UserOutlined />} size={230} shape="square" />
                                  )}
                                </Tooltip>
                              </Col>
                            </Row>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                ) : (
                  ''
                )}
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="stamp_name"
                    label="Stamp Name"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Stamp name is required!',
                        message: 'Stamp name is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter stamp name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="picture"
                    label="Picture"
                    getValueFromEvent={normFile}
                    rules={[
                      {
                        required: true,
                        message: 'Picture is required!',
                        message: 'Picture is required!',
                      },
                    ]}
                  >
                    <Upload
                      name="file"
                      listType="picture"
                      className="create_object"
                      accept={config.FileType.imageType.toString()}
                      beforeUpload={beforeUpload}
                      showUploadList={{
                        showRemoveIcon: stamps?.picture ? false : true,
                        showRemoveIcon: false,
                      }}
                      onPreview={handlePreview}
                      maxCount={1}
                      multiple={false}
                      onChange={uploadFile}
                      fileList={image}
                    >
                      <UploadTrigger fileType="imageType" />
                    </Upload>
                  </Form.Item>
                  <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel}>
                    <div style={{ textAlign: 'center' }}>
                      <img
                        alt="No Thumbnail"
                        style={{
                          width: '100%',
                          height: 'auto',
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                        src={previewImage}
                      />
                    </div>
                  </Modal>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="period_start_date_year"
                    label="Period Start Date"
                    rules={[
                      {
                        required: true,
                        message: 'Period start date is required!',
                        message: 'Period start date is required!',
                      },
                      {
                        validator: validateStartDate, // Custom validation function
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter year" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="period_end_date_year"
                    label="Period End Date"
                    rules={[
                      {
                        required: true,
                        message: 'Period end date is required!',
                      },
                      {
                        validator: validateEndDate, // Custom validation function
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter year" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="source_name"
                    label="Source Name"
                    rules={[
                      {
                        required: true,
                        message: 'Source name is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter source name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="source_reference"
                    label="Source Reference"
                    rules={[
                      {
                        required: true,
                        message: 'Source reference is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter source refernce" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="height"
                    label="Height"
                    rules={[
                      {
                        required: false,
                      },
                      {
                        pattern: /^\d*\.?\d+$/,
                        message: 'Please enter a valid height',
                        message: 'Please enter a valid height',
                      },
                    ]}
                  >
                    <Input placeholder="Enter height" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="width"
                    label="Name line width"
                    rules={[
                      {
                        required: false,
                      },
                      {
                        pattern: /^\d*\.?\d+$/,
                        message: 'Please enter a valid name line width',
                      },
                    ]}
                  >
                    <Input placeholder="Enter name line width" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="city_width"
                    label="City line width"
                    rules={[
                      {
                        required: false,
                      },
                      {
                        pattern: /^\d*\.?\d+$/,
                        message: 'Please enter a valid city line width',
                        message: 'Please enter a valid city line width',
                      },
                    ]}
                  >
                    <Input placeholder="Enter city line width" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="artist" label="Artist / Creator">
                    <Select showSearch allowClear placeholder="Select Artist" optionFilterProp="children">
                      {artist.map(val => (
                        <Select.Option key={val.id} value={val.id}>
                          {val.First_Name} {val.last_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="stamp_notation" label="Stamp Notation">
                    <Input.TextArea placeholder="Enter Stamp Notation" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="description" label="Description">
                    <Input.TextArea placeholder="Enter description" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, `/settings/stamps`)}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton(props.roleType, props.tabs_permissions.settings_stamps, !!stamps ? stamps.id : '')}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default Stamps;
